import React, { useEffect, useState, useCallback } from "react";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  Button,
  Input,
  FormGroup,
  Select,
  TabGroup,
  EmptyState,
  Badge,
  Muted,
} from "../ui";

// ============================================================
// TYPES
// ============================================================

interface ChecklistTemplate {
  id: string;
  name: string;
  checklist_type: "opening" | "closing" | "other";
  items: string[];
  is_active: boolean;
  created_at: string;
}

interface CompletedItem {
  item: string;
  checked: boolean;
  note: string;
}

interface ChecklistCompletion {
  id: string;
  template_id: string;
  template_name: string;
  checklist_type: string;
  completed_items: CompletedItem[];
  notes: string;
  completed_by_name: string;
  created_at: string;
}

interface DrawerCount {
  id: string;
  denominations: Record<string, number>;
  total: number;
  counted_by_name: string;
  created_at: string;
  shift_type: string;
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function ShiftChecklists() {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";

  const tabs = [
    { key: "do", label: "Do Checklist" },
    { key: "history", label: "History" },
    ...(isManager ? [{ key: "manage", label: "Manage" }] : []),
  ];

  const [activeTab, setActiveTab] = useState("do");

  return (
    <PageContainer>
      <PageHeader
        title="Shift Checklists"
        subtitle="Opening and closing shift procedures"
      />
      <TabGroup tabs={tabs} active={activeTab} onChange={setActiveTab} />
      {activeTab === "do" && <DoChecklistTab />}
      {activeTab === "history" && <HistoryTab />}
      {activeTab === "manage" && isManager && <ManageTab />}
    </PageContainer>
  );
}

// ============================================================
// TAB 1: DO CHECKLIST
// ============================================================

function DoChecklistTab() {
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [selected, setSelected] = useState<ChecklistTemplate | null>(null);
  const [items, setItems] = useState<CompletedItem[]>([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const [openRes, closeRes] = await Promise.all([
        api.get("/checklists/templates?type=opening"),
        api.get("/checklists/templates?type=closing"),
      ]);
      const all = [
        ...(Array.isArray(openRes.data) ? openRes.data : []),
        ...(Array.isArray(closeRes.data) ? closeRes.data : []),
      ].filter((t: ChecklistTemplate) => t.is_active);
      setTemplates(all);
    } catch {
      setTemplates([]);
    }
  };

  const startChecklist = (tpl: ChecklistTemplate) => {
    setSelected(tpl);
    setItems(tpl.items.map((item) => ({ item, checked: false, note: "" })));
    setNotes("");
    setDone(false);
  };

  const toggleItem = (idx: number) => {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, checked: !it.checked } : it))
    );
  };

  const setItemNote = (idx: number, note: string) => {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, note } : it))
    );
  };

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await api.post("/checklists/complete", {
        template_id: selected.id,
        completed_items: items,
        notes,
      });
      setDone(true);
    } catch {
      alert("Failed to submit checklist. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen
  if (done && selected) {
    const checked = items.filter((i) => i.checked).length;
    return (
      <Card>
        <div className="text-center py-12">
          <div className="text-5xl mb-4">&#10003;</div>
          <h2
            className="text-xl font-bold mb-2"
            style={{ color: "var(--rbw-navy)" }}
          >
            Checklist Completed
          </h2>
          <p className="mb-1" style={{ color: "var(--rbw-gray-dark)" }}>
            {selected.name} - {checked}/{items.length} items checked
          </p>
          <div className="mt-6">
            <Button
              onClick={() => {
                setSelected(null);
                setDone(false);
              }}
            >
              Back to Checklists
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Active checklist
  if (selected) {
    const checked = items.filter((i) => i.checked).length;
    return (
      <Card>
        <CardHeader
          title={selected.name}
          action={
            <Badge
              variant={
                selected.checklist_type === "opening" ? "gold" : "blue"
              }
            >
              {selected.checklist_type}
            </Badge>
          }
        />
        <div className="space-y-3 mb-6">
          {items.map((it, idx) => (
            <div
              key={idx}
              className="flex items-start gap-3 p-3 rounded-xl transition-colors"
              style={{
                background: it.checked
                  ? "#2D8A4E10"
                  : "var(--rbw-bg)",
                border: `1px solid ${it.checked ? "#2D8A4E30" : "var(--rbw-border)"}`,
              }}
            >
              <button
                onClick={() => toggleItem(idx)}
                className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center transition-colors"
                style={{
                  border: `2px solid ${it.checked ? "var(--rbw-success)" : "var(--rbw-gray)"}`,
                  background: it.checked ? "var(--rbw-success)" : "transparent",
                  color: it.checked ? "#fff" : "transparent",
                }}
              >
                {it.checked && (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div
                  className="text-sm font-medium"
                  style={{
                    color: "var(--rbw-navy)",
                    textDecoration: it.checked ? "line-through" : "none",
                    opacity: it.checked ? 0.6 : 1,
                  }}
                >
                  {it.item}
                </div>
                <input
                  type="text"
                  placeholder="Add a note..."
                  value={it.note}
                  onChange={(e) => setItemNote(idx, e.target.value)}
                  className="mt-1.5 w-full text-xs px-2 py-1.5 rounded-lg outline-none"
                  style={{
                    border: "1px solid var(--rbw-border)",
                    background: "var(--rbw-bg-card)",
                    color: "var(--rbw-navy)",
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mb-6">
          <FormGroup label="Overall Notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes for this shift..."
              rows={3}
              className="w-full px-4 py-2.5 rounded-xl outline-none transition-all text-sm"
              style={{
                border: "2px solid var(--rbw-border)",
                background: "var(--rbw-bg-card)",
                color: "var(--rbw-navy)",
              }}
              onFocus={(e) =>
                (e.target.style.borderColor = "var(--rbw-blue)")
              }
              onBlur={(e) =>
                (e.target.style.borderColor = "var(--rbw-border)")
              }
            />
          </FormGroup>
        </div>

        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setSelected(null)}>
            Cancel
          </Button>
          <div className="flex items-center gap-4">
            <Muted>
              {checked}/{items.length} items checked
            </Muted>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Submitting..." : "Submit Checklist"}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Template selection
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {templates.length === 0 && (
        <div className="col-span-2">
          <Card>
            <EmptyState
              message="No checklists available"
              detail="A manager needs to create checklist templates first."
              icon="&#128203;"
            />
          </Card>
        </div>
      )}
      {templates.map((tpl) => (
        <Card key={tpl.id}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3
                className="text-lg font-bold"
                style={{ color: "var(--rbw-navy)" }}
              >
                {tpl.name}
              </h3>
              <div className="mt-1">
                <Badge
                  variant={
                    tpl.checklist_type === "opening" ? "gold" : "blue"
                  }
                >
                  {tpl.checklist_type}
                </Badge>
              </div>
            </div>
            <div
              className="text-2xl font-bold"
              style={{ color: "var(--rbw-navy)" }}
            >
              {tpl.items.length}
              <span
                className="text-xs font-normal block text-center"
                style={{ color: "var(--rbw-gray-dark)" }}
              >
                items
              </span>
            </div>
          </div>
          <ul className="mb-4 space-y-1">
            {tpl.items.slice(0, 4).map((item, i) => (
              <li
                key={i}
                className="text-sm flex items-center gap-2"
                style={{ color: "var(--rbw-gray-dark)" }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: "var(--rbw-gray)" }}
                />
                {item}
              </li>
            ))}
            {tpl.items.length > 4 && (
              <li className="text-xs" style={{ color: "var(--rbw-gray)" }}>
                +{tpl.items.length - 4} more items
              </li>
            )}
          </ul>
          <Button onClick={() => startChecklist(tpl)} className="w-full">
            Start Checklist
          </Button>
        </Card>
      ))}
    </div>
  );
}

// ============================================================
// TAB 2: HISTORY
// ============================================================

function HistoryTab() {
  const [completions, setCompletions] = useState<ChecklistCompletion[]>([]);
  const [drawerCounts, setDrawerCounts] = useState<DrawerCount[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const [compRes, drawerRes] = await Promise.all([
        api.get("/checklists/completions?limit=30"),
        api.get("/checklists/drawer-counts?limit=30"),
      ]);
      setCompletions(Array.isArray(compRes.data) ? compRes.data : []);
      setDrawerCounts(Array.isArray(drawerRes.data) ? drawerRes.data : []);
    } catch {
      setCompletions([]);
      setDrawerCounts([]);
    }
  };

  // Merge into a single timeline sorted by date
  type TimelineEntry =
    | { type: "checklist"; data: ChecklistCompletion; date: string }
    | { type: "drawer"; data: DrawerCount; date: string };

  const timeline: TimelineEntry[] = [
    ...completions.map(
      (c) =>
        ({ type: "checklist", data: c, date: c.created_at }) as TimelineEntry
    ),
    ...drawerCounts.map(
      (d) =>
        ({ type: "drawer", data: d, date: d.created_at }) as TimelineEntry
    ),
  ].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (timeline.length === 0) {
    return (
      <Card>
        <EmptyState
          message="No history yet"
          detail="Completed checklists and drawer counts will appear here."
          icon="&#128197;"
        />
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {timeline.map((entry) => {
        const id =
          entry.type === "checklist" ? entry.data.id : entry.data.id;
        const isExpanded = expandedId === id;

        if (entry.type === "checklist") {
          const c = entry.data;
          const checkedCount = c.completed_items.filter(
            (i) => i.checked
          ).length;
          return (
            <Card key={`c-${c.id}`} padding="p-0">
              <button
                className="w-full text-left p-5 flex items-center gap-4"
                onClick={() => setExpandedId(isExpanded ? null : id)}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background:
                      c.checklist_type === "opening"
                        ? "#F6BE0015"
                        : "#8BB8E815",
                    color:
                      c.checklist_type === "opening"
                        ? "var(--rbw-gold-dark)"
                        : "var(--rbw-blue-dark)",
                  }}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="font-semibold text-sm"
                      style={{ color: "var(--rbw-navy)" }}
                    >
                      {c.template_name || "Checklist"}
                    </span>
                    <Badge
                      variant={
                        c.checklist_type === "opening" ? "gold" : "blue"
                      }
                    >
                      {c.checklist_type}
                    </Badge>
                  </div>
                  <div
                    className="text-xs mt-0.5"
                    style={{ color: "var(--rbw-gray-dark)" }}
                  >
                    {c.completed_by_name} - {checkedCount}/
                    {c.completed_items.length} items checked
                  </div>
                </div>
                <div
                  className="text-xs flex-shrink-0"
                  style={{ color: "var(--rbw-gray)" }}
                >
                  {formatDate(c.created_at)}
                </div>
                <svg
                  className={`w-4 h-4 transition-transform flex-shrink-0 ${isExpanded ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  style={{ color: "var(--rbw-gray)" }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {isExpanded && (
                <div
                  className="px-5 pb-5 pt-0"
                  style={{ borderTop: "1px solid var(--rbw-border)" }}
                >
                  <div className="pt-4 space-y-2">
                    {c.completed_items.map((it, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 text-sm"
                      >
                        <span
                          style={{
                            color: it.checked
                              ? "var(--rbw-success)"
                              : "var(--rbw-danger)",
                          }}
                        >
                          {it.checked ? "\u2713" : "\u2717"}
                        </span>
                        <div>
                          <span
                            style={{
                              color: "var(--rbw-navy)",
                              opacity: it.checked ? 0.6 : 1,
                              textDecoration: it.checked
                                ? "line-through"
                                : "none",
                            }}
                          >
                            {it.item}
                          </span>
                          {it.note && (
                            <div
                              className="text-xs mt-0.5"
                              style={{ color: "var(--rbw-gray-dark)" }}
                            >
                              Note: {it.note}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {c.notes && (
                      <div
                        className="mt-3 text-sm p-3 rounded-lg"
                        style={{
                          background: "var(--rbw-bg)",
                          color: "var(--rbw-gray-dark)",
                        }}
                      >
                        <strong>Notes:</strong> {c.notes}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          );
        }

        // Drawer count entry
        const d = entry.data;
        return (
          <Card key={`d-${d.id}`} padding="p-0">
            <button
              className="w-full text-left p-5 flex items-center gap-4"
              onClick={() => setExpandedId(isExpanded ? null : id)}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: "#2D8A4E15",
                  color: "var(--rbw-success)",
                }}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className="font-semibold text-sm"
                    style={{ color: "var(--rbw-navy)" }}
                  >
                    Drawer Count
                  </span>
                  <Badge variant="green">
                    ${Number(d.total || 0).toFixed(2)}
                  </Badge>
                </div>
                <div
                  className="text-xs mt-0.5"
                  style={{ color: "var(--rbw-gray-dark)" }}
                >
                  {d.counted_by_name}
                  {d.shift_type ? ` - ${d.shift_type}` : ""}
                </div>
              </div>
              <div
                className="text-xs flex-shrink-0"
                style={{ color: "var(--rbw-gray)" }}
              >
                {formatDate(d.created_at)}
              </div>
              <svg
                className={`w-4 h-4 transition-transform flex-shrink-0 ${isExpanded ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                style={{ color: "var(--rbw-gray)" }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {isExpanded && d.denominations && (
              <div
                className="px-5 pb-5 pt-0"
                style={{ borderTop: "1px solid var(--rbw-border)" }}
              >
                <div className="pt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(d.denominations).map(([denom, qty]) => (
                    <div
                      key={denom}
                      className="flex justify-between text-sm px-3 py-2 rounded-lg"
                      style={{ background: "var(--rbw-bg)" }}
                    >
                      <span style={{ color: "var(--rbw-gray-dark)" }}>
                        {denom}
                      </span>
                      <span
                        className="font-semibold"
                        style={{ color: "var(--rbw-navy)" }}
                      >
                        {qty}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ============================================================
// TAB 3: MANAGE (owner/manager only)
// ============================================================

function ManageTab() {
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [editing, setEditing] = useState<ChecklistTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    checklist_type: "opening" as "opening" | "closing" | "other",
    items: [] as string[],
  });
  const [newItem, setNewItem] = useState("");
  const [saving, setSaving] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      const [openRes, closeRes] = await Promise.all([
        api.get("/checklists/templates?type=opening"),
        api.get("/checklists/templates?type=closing"),
      ]);
      const all = [
        ...(Array.isArray(openRes.data) ? openRes.data : []),
        ...(Array.isArray(closeRes.data) ? closeRes.data : []),
      ];
      setTemplates(all);
    } catch {
      setTemplates([]);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const startCreate = () => {
    setEditing(null);
    setForm({ name: "", checklist_type: "opening", items: [] });
    setNewItem("");
    setCreating(true);
  };

  const startEdit = (tpl: ChecklistTemplate) => {
    setCreating(false);
    setEditing(tpl);
    setForm({
      name: tpl.name,
      checklist_type: tpl.checklist_type,
      items: [...tpl.items],
    });
    setNewItem("");
  };

  const addItem = () => {
    const trimmed = newItem.trim();
    if (!trimmed) return;
    setForm((f) => ({ ...f, items: [...f.items, trimmed] }));
    setNewItem("");
  };

  const removeItem = (idx: number) => {
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const moveItem = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= form.items.length) return;
    setForm((f) => {
      const items = [...f.items];
      const temp = items[idx];
      items[idx] = items[target];
      items[target] = temp;
      return { ...f, items };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim() || form.items.length === 0) {
      alert("Please provide a name and at least one checklist item.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/checklists/templates/${editing.id}`, {
          name: form.name,
          items: form.items,
          is_active: editing.is_active,
        });
      } else {
        await api.post("/checklists/templates", {
          name: form.name,
          checklist_type: form.checklist_type,
          items: form.items,
        });
      }
      setEditing(null);
      setCreating(false);
      loadTemplates();
    } catch {
      alert("Failed to save template.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (tpl: ChecklistTemplate) => {
    try {
      await api.put(`/checklists/templates/${tpl.id}`, {
        name: tpl.name,
        items: tpl.items,
        is_active: !tpl.is_active,
      });
      loadTemplates();
    } catch {
      alert("Failed to update template.");
    }
  };

  // Editor form (create or edit)
  if (creating || editing) {
    return (
      <Card>
        <CardHeader
          title={editing ? `Edit: ${editing.name}` : "New Checklist Template"}
          action={
            <Button variant="ghost" size="sm" onClick={() => { setCreating(false); setEditing(null); }}>
              Cancel
            </Button>
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <FormGroup label="Template Name">
            <Input
              value={form.name}
              onChange={(v) => setForm((f) => ({ ...f, name: v }))}
              placeholder="e.g. Opening Shift Checklist"
            />
          </FormGroup>
          <FormGroup label="Type">
            <Select
              value={form.checklist_type}
              onChange={(v) =>
                setForm((f) => ({
                  ...f,
                  checklist_type: v as "opening" | "closing" | "other",
                }))
              }
            >
              <option value="opening">Opening</option>
              <option value="closing">Closing</option>
              <option value="other">Other</option>
            </Select>
          </FormGroup>
        </div>

        <div className="mb-6">
          <div
            className="text-sm font-semibold mb-2"
            style={{ color: "var(--rbw-navy)" }}
          >
            Checklist Items ({form.items.length})
          </div>
          <div className="space-y-2 mb-4">
            {form.items.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 p-2.5 rounded-xl"
                style={{
                  background: "var(--rbw-bg)",
                  border: "1px solid var(--rbw-border)",
                }}
              >
                <span
                  className="text-xs font-bold w-6 text-center flex-shrink-0"
                  style={{ color: "var(--rbw-gray)" }}
                >
                  {idx + 1}
                </span>
                <span
                  className="flex-1 text-sm"
                  style={{ color: "var(--rbw-navy)" }}
                >
                  {item}
                </span>
                <button
                  onClick={() => moveItem(idx, -1)}
                  disabled={idx === 0}
                  className="p-1 rounded disabled:opacity-20"
                  style={{ color: "var(--rbw-gray-dark)" }}
                  title="Move up"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => moveItem(idx, 1)}
                  disabled={idx === form.items.length - 1}
                  className="p-1 rounded disabled:opacity-20"
                  style={{ color: "var(--rbw-gray-dark)" }}
                  title="Move down"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => removeItem(idx)}
                  className="p-1 rounded transition-colors"
                  style={{ color: "var(--rbw-gray-dark)" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "var(--rbw-danger)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "var(--rbw-gray-dark)")
                  }
                  title="Remove"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}
            {form.items.length === 0 && (
              <div
                className="text-sm py-6 text-center"
                style={{ color: "var(--rbw-gray)" }}
              >
                No items yet. Add your first item below.
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                value={newItem}
                onChange={setNewItem}
                placeholder="New checklist item..."
              />
            </div>
            <Button onClick={addItem} variant="secondary">
              Add Item
            </Button>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : editing ? "Save Changes" : "Create Template"}
          </Button>
        </div>
      </Card>
    );
  }

  // Template list
  return (
    <div>
      <div className="mb-6 flex justify-end">
        <Button onClick={startCreate}>+ New Template</Button>
      </div>
      {templates.length === 0 ? (
        <Card>
          <EmptyState
            message="No checklist templates"
            detail="Create your first checklist template to get started."
            icon="&#128203;"
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {templates.map((tpl) => (
            <Card key={tpl.id}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className="font-bold"
                        style={{ color: "var(--rbw-navy)" }}
                      >
                        {tpl.name}
                      </span>
                      <Badge
                        variant={
                          tpl.checklist_type === "opening"
                            ? "gold"
                            : tpl.checklist_type === "closing"
                              ? "blue"
                              : "default"
                        }
                      >
                        {tpl.checklist_type}
                      </Badge>
                      {!tpl.is_active && (
                        <Badge variant="danger">inactive</Badge>
                      )}
                    </div>
                    <div
                      className="text-xs mt-1"
                      style={{ color: "var(--rbw-gray-dark)" }}
                    >
                      {tpl.items.length} items
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleActive(tpl)}
                  >
                    {tpl.is_active ? "Deactivate" : "Activate"}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => startEdit(tpl)}
                  >
                    Edit
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
